import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import withStyles from '@mui/styles/withStyles';
import { FormControl, FormHelperText } from '@mui/material';
import { StyledEditor, Label } from './TextEditor.styles';
import { WithStylesClassesPropType } from '../../../../lib/commonPropTypes';

function Editor() {
  return <Fragment key="Editor" />;
}
let draftToHtml = () => {};
let htmlToDraft = () => {};

function styles(theme) {
  return {
    formControl: {
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: '2px',
      [`& .rdw-editor-toolbar`]: {
        margin: '-1px'
      }
    },
    formInput: {
      padding: theme.spacing(1),
      maxHeight: '20em',
      ...theme.typography.body1,
      [`& .public-DraftStyleDefault-block`]: {
        margin: '0 !important'
      },
      [`& .rdw-link-decorator-icon`]: {
        left: 'auto',
        right: '-15px'
      },
      [`& .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-listLTR:before`]: {
        whiteSpace: 'nowrap'
      }
    }
  };
}

function TextEditor(props) {
  const { classes, label, name, error, required, value: propValue, onChange, disabled, toolbarHidden } = props;

  const [stateValue, setStateValue] = useState('');
  const [editorState, setEditorState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const onEditorStateChange = (newEditorState) => {
    const value = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));

    setEditorState(newEditorState);
    setStateValue(value);

    onChange({ target: { name, value } });
  };

  const generateStateFromValue = (newValue) => {
    const contentBlock = htmlToDraft(newValue || propValue || '');
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);

    return EditorState.createWithContent(contentState);
  };

  useEffect(() => {
    if (!isLoading && propValue !== stateValue) {
      setStateValue(propValue);
      setEditorState(generateStateFromValue(propValue));
    }
  }, [propValue, stateValue, isLoading]);

  useLayoutEffect(() => {
    /* eslint-disable */
    // Delayed loading because of SSR - Need to wait for the window to be defined
    draftToHtml = require('draftjs-to-html');
    htmlToDraft = require('html-to-draftjs').default;
    Editor = require('react-draft-wysiwyg').Editor;
    /* eslint-enable */

    setIsLoading(!window);
    setStateValue(propValue);
    setEditorState(generateStateFromValue(propValue));
  }, []);

  return isLoading || !editorState ? (
    <p>Editor Loading...</p>
  ) : (
    <FormControl fullWidth error={!!error} required={required}>
      {label ? (
        <Label shrink required={required}>
          {label}
        </Label>
      ) : (
        ''
      )}

      <StyledEditor>
        <Editor
          toolbarHidden={toolbarHidden}
          toolbar={{
            options: ['inline', 'list', 'link', 'history', 'remove'],
            inline: {
              options: ['bold', 'italic', 'strikethrough']
            }
          }}
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          wrapperClassName={classes.formControl}
          editorClassName={classes.formInput}
          stripPastedStyles
          readOnly={disabled}
        />
      </StyledEditor>

      {error && <FormHelperText error>{error}</FormHelperText>}

      <input type="hidden" name={name} value={propValue} />
    </FormControl>
  );
}

TextEditor.propTypes = {
  classes: WithStylesClassesPropType.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  toolbarHidden: PropTypes.bool,
  onChange: PropTypes.func
};

TextEditor.defaultProps = {
  label: '',
  name: 'textEditor',
  value: '',
  error: '',
  required: false,
  disabled: false,
  toolbarHidden: false,
  onChange: () => {}
};

const StyledTextEditor = withStyles(styles)(TextEditor);
export default StyledTextEditor;
